/**
 * The wire format every realtime message shares (see the realtime collaboration contract):
 * `{ v: 1, type, data }`. Anything that arrives over the socket is untrusted, so
 * `parseMessage` checks every field and returns a typed value or null.
 *
 * Who sent a message never comes from the payload. The server stamps the publisher on the
 * publication's `info`, which `memberFromInfo` reads.
 */

export const MESSAGE_VERSION = 1;
export const MAX_FIELD_KEY_LENGTH = 200;
export const MAX_LABEL_LENGTH = 100;

/** `field: null` means the user is not focused on any field. */
export type FocusState = { field: string | null; label?: string };

/** A peer's full ephemeral state. A `state` message replaces all of it. */
export type PeerState = { focus?: FocusState };

export type EntityAction = 'created' | 'updated' | 'deleted';

export type EntityChange = {
  entityType: string;
  entityId: string;
  parentType: string;
  parentId: string;
  action: EntityAction;
};

/** Where a write came from. The server may add values, so subscribers must tolerate any string. */
export type KnownActorSource = 'UI' | 'API' | 'CLI' | 'MCP' | 'AI Assistant' | 'Service Account' | 'Other' | 'System';
export type ActorSource = KnownActorSource | (string & {});

/** `source` is '' when an older server did not send one. */
export type Actor = { userId: number; serviceAccountId: number; agent: boolean; source: ActorSource };

/** Who holds a lock: the connection, the person, and the browser tab. Always sent by the server. */
export type LockHolder = { client: string; id: number; name: string; tab: string };

export type LockChangeReason = 'acquired' | 'released' | 'taken_over' | 'disconnected';

/** `holder` is who has the field now (null when free), `previous` who had it before. */
export type LockChange = {
  field: string;
  holder: LockHolder | null;
  previous: LockHolder | null;
  reason: LockChangeReason;
  /** The channel's sequence number after this change. */
  seq: number;
};

/**
 * What `lock.acquire`, `lock.take_over`, `lock.renew` and `lock.release` answer: the holder after
 * the call, and the channel's sequence number at that point.
 */
export type LockReply = { field: string; holder: LockHolder | null; acquired: boolean; seq: number };

export type ServerMessage =
  | { type: 'entity.changed'; data: EntityChange; actor?: Actor; ts?: number }
  | { type: 'lock.changed'; data: LockChange; ts?: number };

/** Messages a browser may publish on an entity channel. The server rejects anything else there. */
export type ClientMessage = { type: 'focus'; data: FocusState } | { type: 'state'; data: PeerState };

export type Message = ServerMessage | ClientMessage;

/** `avatar` is the person's profile image URL, when they have one. */
export type PeerUser = { id: number; name: string; avatar?: string };

/**
 * One connection in a channel. `tab` is the browser tab's own id: it survives reloads and
 * reconnects (the connection id doesn't), so it keys the tab's color and identity in lists.
 */
export type Member = { clientId: string; tab: string; user: PeerUser };

/** The publisher fields of centrifuge's `ClientInfo` that we read. */
export type TransportInfo = { client: string; user: string; connInfo?: unknown; chanInfo?: unknown };

export function toWire(message: ClientMessage) {
  return { v: MESSAGE_VERSION, type: message.type, data: message.data };
}

type Path = readonly string[];

/** Reads `path` out of untrusted input; undefined when any step is missing. */
function own(root: unknown, path: Path): PropertyDescriptor | undefined {
  let current: unknown = root;
  let descriptor: PropertyDescriptor | undefined;
  for (const key of path) {
    if (typeof current !== 'object' || current === null) return undefined;
    descriptor = Object.getOwnPropertyDescriptor(current, key);
    if (descriptor === undefined) return undefined;
    current = descriptor.value;
  }
  return descriptor;
}

const has = (root: unknown, path: Path) => own(root, path) !== undefined;

function num(root: unknown, path: Path): number | null {
  const found = own(root, path)?.value;
  return typeof found === 'number' && Number.isFinite(found) ? found : null;
}

function str(root: unknown, path: Path, max = Infinity): string | null {
  const found = own(root, path)?.value;
  return typeof found === 'string' && found.length <= max ? found : null;
}

const isNull = (root: unknown, path: Path) => own(root, path)?.value === null && has(root, path);

function idString(root: unknown, path: Path): string | null {
  const found = own(root, path)?.value;
  if (typeof found === 'string') return found;
  return typeof found === 'number' && Number.isFinite(found) ? String(found) : null;
}

const at = (base: Path, key: string): Path => [...base, key];

function parseFocus(raw: unknown, base: Path): FocusState | null {
  const fieldPath = at(base, 'field');
  const key = isNull(raw, fieldPath) ? null : str(raw, fieldPath, MAX_FIELD_KEY_LENGTH);
  if (key === null && !isNull(raw, fieldPath)) return null;

  if (!has(raw, at(base, 'label'))) return { field: key };
  const label = str(raw, at(base, 'label'), MAX_LABEL_LENGTH);
  return label === null ? null : { field: key, label };
}

function parseState(raw: unknown, base: Path): PeerState | null {
  const focusPath = at(base, 'focus');
  if (!has(raw, focusPath)) return {};
  const focus = parseFocus(raw, focusPath);
  return focus === null ? null : { focus };
}

function parseAction(raw: unknown, path: Path): EntityAction | null {
  const action = own(raw, path)?.value;
  return action === 'created' || action === 'updated' || action === 'deleted' ? action : null;
}

const DATA: Path = ['data'];

function parseEntityChange(raw: unknown, base: Path): EntityChange | null {
  const entityType = str(raw, at(base, 'entity_type'));
  const entityId = idString(raw, at(base, 'entity_id'));
  const action = parseAction(raw, at(base, 'action'));
  if (entityType === null || entityType === '' || entityId === null || action === null) return null;
  return {
    entityType,
    entityId,
    parentType: str(raw, at(base, 'parent_type')) ?? '',
    parentId: idString(raw, at(base, 'parent_id')) ?? '',
    action,
  };
}

function parseActor(raw: unknown): Actor | undefined {
  const userId = num(raw, ['actor', 'user_id']);
  if (userId === null) return undefined;
  return {
    userId,
    serviceAccountId: num(raw, ['actor', 'service_account_id']) ?? 0,
    agent: own(raw, ['actor', 'agent'])?.value === true,
    source: str(raw, ['actor', 'source']) ?? '',
  };
}

/** A lock field name, as the server accepts it. */
const LOCK_FIELD = /^[A-Za-z0-9:_!.-]{1,128}$/;

function lockField(raw: unknown, path: Path): string | null {
  const field = str(raw, path);
  return field !== null && LOCK_FIELD.test(field) ? field : null;
}

function parseHolder(raw: unknown, base: Path): LockHolder | null {
  const client = str(raw, at(base, 'client'));
  const id = num(raw, at(base, 'id'));
  const name = str(raw, at(base, 'name'));
  const tab = str(raw, at(base, 'tab'));
  if (client === null || client === '' || id === null || name === null || tab === null) return null;
  return { client, id, name, tab };
}

/** A holder that may be absent: null means the field is free, undefined means the value is malformed. */
function parseNullableHolder(raw: unknown, base: Path): LockHolder | null | undefined {
  if (isNull(raw, base)) return null;
  return parseHolder(raw, base) ?? undefined;
}

/** A channel sequence number: opaque, monotonic, a non-negative integer. */
function seqNumber(raw: unknown, path: Path): number | null {
  const seq = num(raw, path);
  return seq !== null && Number.isInteger(seq) && seq >= 0 ? seq : null;
}

function parseReason(raw: unknown, path: Path): LockChangeReason | null {
  const reason = own(raw, path)?.value;
  const known = reason === 'acquired' || reason === 'released' || reason === 'taken_over' || reason === 'disconnected';
  return known ? reason : null;
}

function parseLockChange(raw: unknown, base: Path): LockChange | null {
  const field = lockField(raw, at(base, 'field'));
  const holder = parseNullableHolder(raw, at(base, 'holder'));
  const previous = parseNullableHolder(raw, at(base, 'previous'));
  const reason = parseReason(raw, at(base, 'reason'));
  const seq = seqNumber(raw, at(base, 'seq'));
  if (field === null || holder === undefined || previous === undefined || reason === null || seq === null) return null;
  return { field, holder, previous, reason, seq };
}

/** Validates the answer to a lock RPC; null when malformed. */
export function parseLockReply(raw: unknown): LockReply | null {
  const field = lockField(raw, ['field']);
  const holder = parseNullableHolder(raw, ['holder']);
  const acquired = own(raw, ['acquired'])?.value;
  const seq = seqNumber(raw, ['seq']);
  if (field === null || holder === undefined || typeof acquired !== 'boolean' || seq === null) return null;
  return { field, holder, acquired, seq };
}

export type LockEntry = { field: string; holder: LockHolder };

export type LockList = { locks: LockEntry[]; seq: number };

/** Validates the answer to `lock.list`; null when the shape or `seq` is wrong, and malformed entries are dropped. */
export function parseLockList(raw: unknown): LockList | null {
  const entries = own(raw, ['locks'])?.value;
  const seq = seqNumber(raw, ['seq']);
  if (!Array.isArray(entries) || seq === null) return null;
  const locks = entries.flatMap((entry: unknown) => {
    const field = lockField(entry, ['field']);
    const holder = parseHolder(entry, ['holder']);
    return field === null || holder === null ? [] : [{ field, holder }];
  });
  return { locks, seq };
}

function parseServerMessage(raw: unknown): Message | null {
  const change = parseEntityChange(raw, DATA);
  if (change === null) return null;
  return { type: 'entity.changed', data: change, actor: parseActor(raw), ts: num(raw, ['ts']) ?? undefined };
}

/** Validates a publication payload. Returns null for anything that is not a well-formed v1 message. */
export function parseMessage(raw: unknown): Message | null {
  if (num(raw, ['v']) !== MESSAGE_VERSION) return null;
  switch (str(raw, ['type'])) {
    case 'entity.changed':
      return parseServerMessage(raw);
    case 'lock.changed': {
      const change = parseLockChange(raw, DATA);
      return change === null ? null : { type: 'lock.changed', data: change, ts: num(raw, ['ts']) ?? undefined };
    }
    case 'focus': {
      const focus = parseFocus(raw, DATA);
      return focus === null ? null : { type: 'focus', data: focus };
    }
    case 'state': {
      const state = parseState(raw, DATA);
      return state === null ? null : { type: 'state', data: state };
    }
    default:
      return null;
  }
}

const MAX_AVATAR_LENGTH = 2048;

/**
 * An avatar URL from the wire is untrusted: only http(s) and same-origin relative paths
 * ("/images/a.png") are kept, so it can never be a `javascript:` or `data:` URL. Anything
 * else returns undefined and the editor falls back to initials.
 */
export function safeAvatarUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw === '' || raw.length > MAX_AVATAR_LENGTH) return undefined;
  if (raw.startsWith('/')) return raw.startsWith('//') || raw.includes('\\') ? undefined : raw;
  try {
    const { protocol } = new URL(raw);
    return protocol === 'https:' || protocol === 'http:' ? raw : undefined;
  } catch {
    return undefined;
  }
}

function parseUser(raw: unknown): PeerUser | null {
  const id = num(raw, ['id']);
  const name = str(raw, ['name']);
  if (id === null || name === null) return null;
  const avatar = safeAvatarUrl(own(raw, ['avatar'])?.value);
  return avatar === undefined ? { id, name } : { id, name, avatar };
}

/**
 * Who a connection is, from the identity the server attached at connect time
 * (`connInfo`, or `chanInfo` for a per-channel override).
 */
export function memberFromInfo(info: TransportInfo | undefined): Member | null {
  if (info === undefined) return null;
  const user = parseUser(info.connInfo) ?? parseUser(info.chanInfo);
  if (user === null) return null;
  const tab = str(info.connInfo, ['tab']);
  return { clientId: info.client, tab: tab === null || tab === '' ? info.client : tab, user };
}
